import { phone as validatePhoneNumber, countryPhoneData } from 'phone';

export interface PhoneCountryOption {
  value: string;
  label: string;
  dialCode: string;
}

export const DEFAULT_PHONE_COUNTRY = 'US';

export const PHONE_COUNTRY_OPTIONS: PhoneCountryOption[] = countryPhoneData
  .map((country) => ({
    value: country.alpha2,
    label: country.country_name,
    dialCode: `+${country.country_code}`,
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

const REGIONAL_INDICATOR_OFFSET = 0x1f1e6 - 'A'.charCodeAt(0);

/**
 * The Unicode flag for a two-letter country code (`'US'` → 🇺🇸), or `''` for
 * anything that isn't two A–Z letters. Emoji rather than image sprites, which
 * would need hundreds of bundled SVGs this app doesn't carry.
 */
export function flagEmoji(alpha2: string): string {
  const code = alpha2.toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) {
    return '';
  }
  return String.fromCodePoint(
    code.charCodeAt(0) + REGIONAL_INDICATOR_OFFSET,
    code.charCodeAt(1) + REGIONAL_INDICATOR_OFFSET,
  );
}

// Validate a raw phone number entered for the given country and return its
// canonical E.164 form (e.g. "+447911123456"), or '' when empty or invalid.
// An empty country falls back to `DEFAULT_PHONE_COUNTRY` — the fallback lives
// here, not at the call sites, so field validation and the submitted payload
// always apply identical country rules.
// The `phone` library applies per-country knowledge — stripping trunk zeros
// ("07911123456") and honoring pre-included dial codes ("+447911123456") —
// that naive dial-code concatenation cannot. `validateMobilePrefix: false`
// accepts both mobile and landline numbers, which the library otherwise
// rejects for countries (e.g. GB) that distinguish them.
export function normalizePhoneNumber(countryAlpha2: string, phoneNumber: string | undefined): string {
  if (phoneNumber == null || phoneNumber.trim() === '') {
    return '';
  }

  const country = countryAlpha2 === '' ? DEFAULT_PHONE_COUNTRY : countryAlpha2;
  // Keep the result object rather than destructuring so `isValid` narrows the
  // `PhoneResult` union and `phoneNumber` is typed `string` in the valid branch.
  const result = validatePhoneNumber(phoneNumber, { country, validateMobilePrefix: false });
  return result.isValid ? result.phoneNumber : '';
}

// Form validator for a phone field: returns `true` when valid, or a user-facing
// error message. Validity is decided by `normalizePhoneNumber`, so the field
// validation and the submitted payload always apply identical country rules.
export function validatePhoneField(countryAlpha2: string, phoneNumber: string | undefined): string | true {
  if (phoneNumber == null || phoneNumber.trim() === '') {
    return 'Enter a phone number';
  }

  if (normalizePhoneNumber(countryAlpha2, phoneNumber) === '') {
    return 'Enter a valid phone number';
  }

  return true;
}
